import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { cartAPI, productsAPI } from '../services/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const CartContext = createContext();

const initialState = {
  items: [],
  totalItems: 0,
  totalPrice: 0,
  loading: false,
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CART':
      return {
        ...state,
        items: action.payload.items || [],
        totalItems: action.payload.totalItems || 0,
        totalPrice: action.payload.totalPrice || 0,
        loading: false,
      };
    case 'ADD_TO_CART':
      return {
        ...state,
        loading: false,
      };
    case 'UPDATE_CART':
      return {
        ...state,
        loading: false,
      };
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        loading: false,
      };
    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        totalItems: 0,
        totalPrice: 0,
        loading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { isAuthenticated } = useAuth();

  // Sepeti localStorage'dan yükle (misafir kullanıcılar için)
  const loadGuestCart = () => {
    try {
      const savedCart = localStorage.getItem('guestCart');
      if (savedCart) {
        const cartData = JSON.parse(savedCart);
        dispatch({
          type: 'SET_CART',
          payload: {
            items: cartData.items || [],
            totalItems: cartData.totalItems || 0,
            totalPrice: cartData.totalPrice || 0
          }
        });
      }
    } catch (error) {
      // Hata durumunda sessizce devam et
    }
  };

  // Sepeti localStorage'a kaydet (misafir kullanıcılar için)
  const saveGuestCart = (cartData) => {
    try {
      localStorage.setItem('guestCart', JSON.stringify({
        items: cartData.items || [],
        totalItems: cartData.totalItems || 0,
        totalPrice: cartData.totalPrice || 0
      }));
    } catch (error) {
      // Hata durumunda sessizce devam et
    }
  };

  // Kullanıcı giriş yaptığında sepeti yükle
  useEffect(() => {
    if (isAuthenticated) {
      loadCart();
      // Giriş yapıldığında localStorage sepetini temizle
      localStorage.removeItem('guestCart');
    } else {
      // Misafir kullanıcı için localStorage'dan sepeti yükle
      loadGuestCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadCart = async (retryCount = 0) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await cartAPI.getCart();
      dispatch({
        type: 'SET_CART',
        payload: response.data.cart,
      });
    } catch (error) {
      console.error('Sepet yüklenemedi:', error);
      
      // Network hatası ve retry sayısı 2'den az ise tekrar dene
      if (error.code === 'NETWORK_ERROR' && retryCount < 2) {
        console.log(`Retrying loadCart, attempt ${retryCount + 1}`);
        setTimeout(() => loadCart(retryCount + 1), 1500); // 1.5 saniye bekle
        return;
      }
      
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      if (isAuthenticated) {
        // Giriş yapmış kullanıcı - backend'e kaydet
        await cartAPI.addToCart(productId, quantity);
        await loadCart();
      } else {
        // Misafir kullanıcı - localStorage'a kaydet
        const product = await getProductInfo(productId);
        if (!product) {
          throw new Error('Ürün bulunamadı');
        }

        const currentItems = [...state.items];
        const existingItemIndex = currentItems.findIndex(
          item => item.product_id === productId
        );

        if (existingItemIndex !== -1) {
          currentItems[existingItemIndex].quantity += quantity;
        } else {
          currentItems.push({
            product_id: productId,
            quantity: quantity,
            product: {
              id: product.id,
              name: product.name,
              price: product.price,
              images: product.images
            }
          });
        }

        const totalItems = currentItems.reduce((sum, item) => sum + item.quantity, 0);
        const totalPrice = currentItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);

        const cartData = {
          items: currentItems,
          totalItems,
          totalPrice
        };

        dispatch({
          type: 'SET_CART',
          payload: cartData
        });
        saveGuestCart(cartData);
      }
      
      dispatch({ type: 'SET_LOADING', payload: false });
      toast.success('Ürün sepete eklendi!');
      return { success: true };
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      const message = error.response?.data?.message || error.message || 'Ürün sepete eklenemedi';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Ürün bilgisini getir (misafir sepeti için)
  const getProductInfo = async (productId) => {
    try {
      const response = await productsAPI.getProduct(productId);
      return response.data.product;
    } catch (error) {
      return null;
    }
  };

  const updateCartItem = async (productId, quantity) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      if (isAuthenticated) {
        await cartAPI.updateCartItem(productId, quantity);
        await loadCart();
      } else {
        // Misafir kullanıcı - localStorage'da güncelle
        const currentItems = [...state.items];
        const itemIndex = currentItems.findIndex(item => item.product_id === productId);
        
        if (itemIndex !== -1) {
          if (quantity <= 0) {
            currentItems.splice(itemIndex, 1);
          } else {
            currentItems[itemIndex].quantity = quantity;
          }

          const totalItems = currentItems.reduce((sum, item) => sum + item.quantity, 0);
          const totalPrice = currentItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);

          const cartData = {
            items: currentItems,
            totalItems,
            totalPrice
          };

          dispatch({
            type: 'SET_CART',
            payload: cartData
          });
          saveGuestCart(cartData);
        }
      }
      
      dispatch({ type: 'SET_LOADING', payload: false });
      toast.success('Sepet güncellendi!');
      return { success: true };
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      const message = error.response?.data?.message || 'Sepet güncellenemedi';
      toast.error(message);
      return { success: false, message };
    }
  };

  const removeFromCart = async (productId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      if (isAuthenticated) {
        await cartAPI.removeFromCart(productId);
        await loadCart();
      } else {
        // Misafir kullanıcı - localStorage'dan çıkar
        const currentItems = state.items.filter(item => item.product_id !== productId);
        const totalItems = currentItems.reduce((sum, item) => sum + item.quantity, 0);
        const totalPrice = currentItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);

        const cartData = {
          items: currentItems,
          totalItems,
          totalPrice
        };

        dispatch({
          type: 'SET_CART',
          payload: cartData
        });
        saveGuestCart(cartData);
      }
      
      dispatch({ type: 'SET_LOADING', payload: false });
      toast.success('Ürün sepetten çıkarıldı!');
      return { success: true };
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      const message = error.response?.data?.message || 'Ürün çıkarılamadı';
      toast.error(message);
      return { success: false, message };
    }
  };

  const clearCart = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      if (isAuthenticated) {
        await cartAPI.clearCart();
      } else {
        localStorage.removeItem('guestCart');
      }
      
      dispatch({ type: 'CLEAR_CART' });
      dispatch({ type: 'SET_LOADING', payload: false });
      toast.success('Sepet temizlendi!');
      return { success: true };
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      const message = error.response?.data?.message || 'Sepet temizlenemedi';
      toast.error(message);
      return { success: false, message };
    }
  };

  const getCartCount = async () => {
    try {
      const response = await cartAPI.getCartCount();
      return response.data.count;
    } catch (error) {
      return 0;
    }
  };

  const value = {
    ...state,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    loadCart,
    getCartCount,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

